import React, { FC } from 'react';
import { GetStaticProps, GetStaticPropsContext } from 'next';
import { DynamicComponent } from '@/components';
import { TypeHomePage, TypeRedesignHomepage } from '@/core/generated-types';
import { loadLayout } from '@/utils/loadLayout';
import { ContentfulPreviewData, getEntries } from '@/lib/contentful';
import { Seo } from '@/components/ui/Seo';
import {
  buildCanonicalUrl,
  defaultCacheRevalidation,
} from '@/core/contentfulConfig';
import { locales } from '@/core/constants';
export interface Props {
  title: string;
  page: TypeHomePage;
  redesignPage: TypeRedesignHomepage;
  isHomepageRedesignExperimentEnable: boolean;
}

export const getStaticPaths = () => {
  const paths = locales.map((locale) => ({
    params: {
      slug: '/',
      locale,
    },
  }));
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({
  preview = false,
  previewData,
  params,
}: GetStaticPropsContext) => {
  const { locale } = params as { locale: string };
  const previewDataPayload = previewData as ContentfulPreviewData;

  const [page, layout] = await Promise.all([
    getEntries(
      { 'fields.slug': 'home', locale, contentType: 'homePage' },
      preview,
      previewDataPayload,
    ),
    loadLayout(locale, preview, previewDataPayload),
  ]);

  const { globalNav, header, footer, footerLegal } = layout;

  if (page?.items.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page: page?.items?.[0] || null,
      globalNav,
      header,
      footer,
      footerLegal,
    },
    revalidate: defaultCacheRevalidation,
  };
};

const Home: FC<Props> = ({ page }) => {
  const {
    fields: { heroSection, components, metadata },
  } = page;
  const metadataFields = metadata?.fields;

  return (
    <>
      <Seo
        title={metadataFields?.pageTitle}
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow}
        noindex={metadataFields?.noIndex}
        canonical={buildCanonicalUrl(page)}
      />

      <DynamicComponent component={heroSection} />
      {components?.map((component, i) => (
        <DynamicComponent
          key={`${component.sys.id}-${i}`}
          component={component}
        />
      ))}
    </>
  );
};

export default Home;
